import {
  fileContentUrl,
  type LibraryFile,
  type LibraryFileType,
} from '@/services/DataService';
import { FILE_THUMBNAIL_SIZE } from '@/constants/fileUpload';
import {
  LIBRARY_DOCUMENT_TILE_RATIO,
  LIBRARY_TILE_MAX_RATIO,
  LIBRARY_TILE_MIN_RATIO,
  LIBRARY_TILE_UNKNOWN_RATIO,
} from '@/constants/library';
import type { PendingUpload } from '@/hooks/useLibraryFiles';

/**
 * One thing the library shows: a stored file, or one still uploading. The two
 * are drawn by the same tiles and rows, so they share what drawing needs.
 */
export type LibraryEntry =
  | { kind: 'file'; id: string; file: LibraryFile }
  | { kind: 'pending'; id: string; upload: PendingUpload };

/** What every entry has, file or upload. */
interface EntryShape {
  filename: string;
  mime_type: string;
  size_bytes: number;
  width: number | null;
  height: number | null;
}

export const fileEntry = (file: LibraryFile): LibraryEntry => ({ kind: 'file', id: file.id, file });

export const pendingEntry = (upload: PendingUpload): LibraryEntry => ({ kind: 'pending', id: upload.id, upload });

export const entryShape = (entry: LibraryEntry): EntryShape =>
  (entry.kind === 'file' ? entry.file : entry.upload);

export const isImageType = (mimeType: string): boolean => mimeType.startsWith('image/');

/**
 * Whether a file of this mime type belongs under a type filter, as the server
 * narrows the list: an upload in progress is held to the same rule.
 *
 * @param mimeType - The file's mime type.
 * @param type - The type filter, or null for every type.
 *
 * @returns True when it belongs.
 */
export const matchesType = (mimeType: string, type: LibraryFileType | null): boolean => {
  if (type === 'image') return isImageType(mimeType);
  if (type === 'pdf') return mimeType === 'application/pdf';
  return true;
};

const TYPE_LABELS: Record<string, string> = {
  'image/png': 'PNG',
  'image/jpeg': 'JPEG',
  'image/gif': 'GIF',
  'image/webp': 'WebP',
  'application/pdf': 'PDF',
};

/**
 * The short name of a file's type, as a list column shows it.
 *
 * @param mimeType - The file's mime type.
 *
 * @returns `PNG`, `PDF` and so on; the subtype in capitals for anything else.
 */
export const typeLabel = (mimeType: string): string =>
  TYPE_LABELS[mimeType] ?? (mimeType.split('/')[1]?.toUpperCase() || 'File');

/**
 * A tile's height over its width: an image's own shape, held between limits;
 * a square for an image whose size is not known; a fixed card for anything
 * else.
 *
 * @param entry - The entry.
 *
 * @returns The ratio.
 */
export const tileRatio = (entry: LibraryEntry): number => {
  const { mime_type: mimeType, width, height } = entryShape(entry);
  if (!isImageType(mimeType)) {
    return LIBRARY_DOCUMENT_TILE_RATIO;
  }
  if (!width || !height) {
    return LIBRARY_TILE_UNKNOWN_RATIO;
  }
  return Math.min(Math.max(height / width, LIBRARY_TILE_MIN_RATIO), LIBRARY_TILE_MAX_RATIO);
};

/**
 * The `srcSet` a grid tile loads from: the tile size and the next one up, so a
 * dense screen or a wide column picks the sharper one and a plain screen the
 * lighter one. Both fall back to the original for an image with no
 * thumbnails, which the browser then scales.
 *
 * @param fileId - The file.
 *
 * @returns The srcSet.
 */
export const tileSrcSet = (fileId: string): string => [
  `${fileContentUrl(fileId, FILE_THUMBNAIL_SIZE.TILE)} ${FILE_THUMBNAIL_SIZE.TILE}w`,
  `${fileContentUrl(fileId, FILE_THUMBNAIL_SIZE.INLINE)} ${FILE_THUMBNAIL_SIZE.INLINE}w`,
].join(', ');

/**
 * Save a file. The URL answers with `Content-Disposition: attachment`, so the
 * browser downloads it under its own name and the page stays where it is.
 *
 * @param url - The file's download URL.
 */
export const startDownload = (url: string): void => {
  const link = document.createElement('a');
  link.href = url;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/** A width and a height, in pixels. */
export interface Size {
  width: number;
  height: number;
}

/**
 * The size an image is drawn at to fit whole inside an area: its own shape,
 * scaled down until it fits, and never scaled up past its own size — unless
 * `enlarge`, for a size that is only a shape (a stand-in ratio), which then
 * fills the room.
 *
 * @param size - The image's own size, or a shape.
 * @param area - The room it has.
 * @param options - `enlarge` to scale a shape up to the room.
 *
 * @returns The size to draw it at, or null when either has no size to work with.
 */
export const fitWithin = (size: Size, area: Size, { enlarge = false } = {}): Size | null => {
  if (size.width <= 0 || size.height <= 0 || area.width <= 0 || area.height <= 0) {
    return null;
  }
  const fit = Math.min(area.width / size.width, area.height / size.height);
  const scale = enlarge ? fit : Math.min(fit, 1);
  return { width: Math.round(size.width * scale), height: Math.round(size.height * scale) };
};
